import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixInputGroupComponent } from "@app/shared/molecules/cerrix-input-group/cerrix-input-group.component";
import { CerrixTextareaGroupComponent } from "@app/shared/molecules/cerrix-textarea-group/cerrix-textarea-group.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { UpdateMetaDataModel } from "@models/assetmanagement/updatemetadatamodel";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { finalize, Observable, firstValueFrom } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import {
    CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
    CerrixSlideOutEditItemSection,
} from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-section.model";
import { CerrixSelectGroupComponent } from "@app/shared/molecules/cerrix-select-group/cerrix-select-group.component";
import { CommonModule } from "@angular/common";
import { ThirdPartyDataService } from "@pages/thirdparty/services/ThirdPartyDataService";
import { ThirdPartyContractDataService } from "@pages/thirdparty/services/ThirdPartyContractDataService";
import { toDisplayValue } from "@methods/CommonMethods";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";

@Component({
    selector: "app-edit-asset-general-information",
    templateUrl: "./edit-asset-general-information.component.html",
    styleUrls: ["./edit-asset-general-information.component.scss"],
    imports: [
        TranslatePipe,
        CommonModule,
        CerrixInstructionsIconComponent,
        CerrixInputGroupComponent,
        CerrixTextareaGroupComponent,
        CerrixButtonComponent,
        CerrixSelectGroupComponent,
        CerrixInputValidationRequiredDirective,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        CerrixInputValidationRegistryService,
        {
            provide: CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
            useExisting: EditAssetGeneralInformationComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetGeneralInformationComponent
    implements OnInit, CerrixSlideOutEditItemSection
{
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: UpdateMetaDataModel;
    protected editSaving = false;
    protected optionsLoaded = false;

    protected thirdPartyOptions: { label: string; value: string }[] = [];
    protected contractOptions: { label: string; value: string }[] = [];
    protected showThirdPartySelection = false;
    protected contractsLoading = false;

    private _emptyThirdPartyOption: { label: string; value: string };
    private _emptyContractOption: { label: string; value: string };
    private _noThirdPartyOption: { label: string; value: string };
    private _noContractOption: { label: string; value: string };

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _toastService = inject(ToastrService);
    private _thirdPartyDs = inject(ThirdPartyDataService);
    private _contractDs = inject(ThirdPartyContractDataService);

    async ngOnInit() {
        // Before the await: the slide-out can call hasUnsavedChanges() while the scope loads.
        this.revertChanges();

        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();
        this._emptyThirdPartyOption = {
            label: this._translation.t(
                "edit-general-information.selectThirdPartyPlaceholder",
            ),
            value: "",
        };
        this._emptyContractOption = {
            label: this._translation.t(
                "edit-general-information.selectContractPlaceholder",
            ),
            value: "",
        };
        this._noThirdPartyOption = {
            label: this._translation.t(
                "edit-general-information.noThirdPartyPlaceholder",
            ),
            value: "",
        };
        this._noContractOption = {
            label: this._translation.t(
                "edit-general-information.noContractsPlaceholder",
            ),
            value: "",
        };

        this.thirdPartyOptions = [this._noThirdPartyOption];
        this.contractOptions = [this._noContractOption];

        await this.loadThirdParties();
        if (this.editModel.thirdPartyId) {
            await this.loadContractsForThirdParty(this.editModel.thirdPartyId);
        }
        this.optionsLoaded = true;
    }

    protected get isContractDisabled(): boolean {
        return (
            !this.editModel?.thirdPartyId ||
            this.contractsLoading ||
            this.contractOptions.length <= 1
        );
    }

    protected async onThirdPartyChange(value: string): Promise<void> {
        this.contractOptions = [this._noContractOption];
        this.editModel.thirdPartyId = value || null;
        this.editModel.contractId =
            this.editModel.thirdPartyId == this.model.thirdPartyInformation?.thirdPartyId
                ? this.model.thirdPartyInformation?.contractId
                : null;

        if (this.editModel.thirdPartyId) {
            await this.loadContractsForThirdParty(this.editModel.thirdPartyId);
        } else {
            this.contractsLoading = false;
        }
    }

    protected onContractChange(value: string): void {
        this.editModel.contractId = value || null;
    }

    public async save(): Promise<boolean> {
        if (!this.validateFields()) {
            return false;
        }

        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._translation.t("detail.savingChanges"),
        );
        const storedAsset = await firstValueFrom(
            this.editSaveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                    this._tabService.refresh(this._pages.AssetManagementOverview);
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset, {
                thirdPartyInformation: {
                    thirdPartyId: storedAsset.thirdPartyInformation?.thirdPartyId,
                    contractId: storedAsset.thirdPartyInformation?.contractId,
                },
            });
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._translation.t("detail.updateCompleted"),
            );
            return true;
        }
        return false;
    }

    public hasUnsavedChanges(): boolean {
        return (
            this.model.name !== this.editModel.name ||
            this.model.description !== this.editModel.description ||
            (this.model.thirdPartyInformation?.thirdPartyId ?? null) !==
                (this.editModel.thirdPartyId ?? null) ||
            (this.model.thirdPartyInformation?.contractId ?? null) !==
                (this.editModel.contractId ?? null)
        );
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._translation.t(
                                "detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._translation.t("detail.saveFailed"),
            );
            return false;
        }
        return true;
    }

    private editSaveSection(): Observable<AssetProjection> {
        return this._ds.vbetaUpdateAssetMetaData(this.model.guid, {
            assetName: this.editModel.name,
            description: this.editModel.description,
            version: this.editModel.version,
            thirdPartyId: this.editModel.thirdPartyId ?? null,
            contractId: this.editModel.contractId ?? null,
        });
    }

    private revertChanges(): void {
        this.editModel = Object.assign(new UpdateMetaDataModel(), this.model, {
            thirdPartyId: this.model.thirdPartyInformation?.thirdPartyId,
            contractId: this.model.thirdPartyInformation?.contractId,
        });
    }

    private async loadThirdParties(): Promise<void> {
        let tpoptions = [this._noThirdPartyOption];
        try {
            const thirdPartiesToInclude = this.model.thirdPartyInformation?.thirdPartyId
                ? [this.model.thirdPartyInformation?.thirdPartyId]
                : [];
            const tps = await firstValueFrom(
                this._thirdPartyDs.getThirdPartiesMetaData(thirdPartiesToInclude),
            );
            if (tps && tps.length > 0) {
                tpoptions = [
                    this._emptyThirdPartyOption,
                    ...tps.map((tp) => ({
                        label: toDisplayValue(tp.name, tp.archived),
                        value: tp.id,
                    })),
                ];
            }
        } catch {
            // tpoptions stays empty; fallback UI handled via showThirdPartySelection below
            this._toastService.error(
                this._translation.t(
                    "edit-general-information.loadThirdPartiesFailed",
                ),
            );
        }
        this.thirdPartyOptions = tpoptions;
        this.showThirdPartySelection = tpoptions.length >= 1;
    }

    private async loadContractsForThirdParty(thirdPartyGuid: string): Promise<void> {
        this.contractsLoading = true;
        try {
            const contractsToInclude = this.model.thirdPartyInformation?.contractId
                ? [this.model.thirdPartyInformation?.contractId]
                : [];
            const contracts = await firstValueFrom(
                this._contractDs.getContractsMetadata(thirdPartyGuid, contractsToInclude),
            );
            this.contractOptions = contracts?.length
                ? [
                      this._emptyContractOption,
                      ...contracts
                          //the linked contract might be archived but we still want to show it;
                          //we use the original model because the editModel is updated when we switch between TP's
                          .filter(
                              (c) =>
                                  c.archived === false ||
                                  c.id === this.model.thirdPartyInformation?.contractId,
                          )
                          .map((c) => ({
                              label: toDisplayValue(c.name, c.archived),
                              value: c.id,
                          })),
                  ]
                : [this._noContractOption];
        } catch {
            this._toastService.error(
                this._translation.t(
                    "edit-general-information.loadThirdPartyContractsFailed",
                ),
            );
            this.contractOptions = [this._noContractOption];
        } finally {
            this.contractsLoading = false;
        }
    }
}
